import { useState, useEffect } from 'react';
import api from '../../services/api';
import StatusBadge from '../../components/shared/StatusBadge';

const PL_STYLES = {
  'RISET': { bg: 'rgba(129,140,248,0.08)', border: 'rgba(129,140,248,0.18)', color: '#818cf8' },
  'PENT TEST': { bg: 'rgba(251,113,133,0.08)', border: 'rgba(251,113,133,0.18)', color: '#fb7185' },
  'TEST BENCH': { bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.18)', color: '#f59e0b' },
};

function getPdfUrl(pdfPath) {
  if (!pdfPath) return null;
  const filename = pdfPath.split('/').pop();
  return '/uploads/certificates/' + filename;
}

export default function Licenses() {
  const [licenses, setLicenses] = useState([]);
  const [certificates, setCertificates] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    async function load() {
      try {
        const [licRes, certRes] = await Promise.all([
          api.get('/licenses?limit=100'),
          api.get('/certificates?limit=100'),
        ]);
        setLicenses(licRes.data.licenses || []);
        const certMap = {};
        const certs = certRes.data.certificates || certRes.data || [];
        certs.forEach(c => {
          if (!certMap[c.applianceId]) certMap[c.applianceId] = [];
          certMap[c.applianceId].push(c);
        });
        setCertificates(certMap);
      } catch (err) {
        console.error('Load error:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:200}}>
      <div style={{width:32,height:32,border:'3px solid rgba(129,140,248,0.3)',borderTopColor:'#818cf8',borderRadius:'50%',animation:'spin 1s linear infinite'}}/>
    </div>
  );

  const grouped = {};
  licenses.forEach(l => {
    const plName = l.productLine?.name || 'Other';
    if (!grouped[plName]) grouped[plName] = { productLine: l.productLine, appliances: {} };
    const appKey = plName + '-' + l.applianceId;
    if (!grouped[plName].appliances[appKey]) {
      grouped[plName].appliances[appKey] = {
        id: l.applianceId,
        name: l.appliance?.unitName || 'Unknown',
        serial: l.appliance?.serialNumber,
        plName,
        licenses: [],
      };
    }
    grouped[plName].appliances[appKey].licenses.push(l);
  });

  return (
    <div style={{padding:'24px',maxWidth:960,margin:'0 auto'}}>
      <h1 style={{fontSize:24,fontWeight:700,marginBottom:24,color:'#f1f5f9'}}>My Licenses</h1>

      {Object.entries(grouped).map(([plName, plData]) => {
        const st = PL_STYLES[plName] || PL_STYLES['RISET'];
        return (
          <div key={plName} style={{marginBottom:32}}>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16,padding:'10px 16px',background:st.bg,border:'1px solid ' + st.border,borderRadius:10}}>
              <span style={{fontWeight:700,fontSize:15,color:st.color}}>{plName}</span>
              <span style={{fontSize:12,color:'#94a3b8',marginLeft:'auto'}}>{Object.keys(plData.appliances).length} units</span>
            </div>

            {Object.entries(plData.appliances).map(([appKey, app]) => {
              const appCerts = certificates[app.id] || [];
              const isOpen = expanded[appKey];
              return (
                <div key={appKey} style={{marginBottom:12,background:'rgba(15,23,42,0.6)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:12,overflow:'hidden'}}>
                  <div
                    onClick={() => setExpanded(p => ({...p, [appKey]: !p[appKey]}))}
                    style={{display:'flex',alignItems:'center',gap:12,padding:'14px 20px',cursor:'pointer',userSelect:'none'}}
                  >
                    <div style={{width:36,height:36,background:st.bg,border:'1px solid ' + st.border,borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center'}}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={st.color} strokeWidth="2">
                        <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
                      </svg>
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:600,fontSize:15,color:'#f1f5f9'}}>{app.name}</div>
                      <div style={{fontSize:12,color:'#64748b',fontFamily:'monospace'}}>{app.serial}</div>
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <span style={{fontSize:12,color:'#64748b'}}>{app.licenses.length} licenses</span>
                      {appCerts.length > 0 && (
                        <span style={{fontSize:11,background:'rgba(129,140,248,0.15)',color:'#818cf8',padding:'2px 8px',borderRadius:20,border:'1px solid rgba(129,140,248,0.3)'}}>
                          {appCerts.length} cert{appCerts.length > 1 ? 's' : ''}
                        </span>
                      )}
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" style={{transform:isOpen?'rotate(180deg)':'none',transition:'transform 0.2s'}}>
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </div>
                  </div>

                  {isOpen && (
                    <div style={{borderTop:'1px solid rgba(255,255,255,0.06)',padding:'16px 20px'}}>
                      <div style={{marginBottom:16}}>
                        <div style={{fontSize:11,fontWeight:600,color:'#64748b',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:8}}>License Keys</div>
                        {app.licenses.map(lic => (
                          <div key={lic.id} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',background:'rgba(255,255,255,0.03)',borderRadius:8,marginBottom:6}}>
                            <div style={{flex:1,fontSize:13,color:'#cbd5e1'}}>{lic.softwareName}</div>
                            <div style={{fontFamily:'monospace',fontSize:13,color:'#818cf8',letterSpacing:'0.05em'}}>{lic.licenseKey}</div>
                            <StatusBadge status={lic.status}/>
                          </div>
                        ))}
                      </div>

                      {appCerts.length > 0 && (
                        <div>
                          <div style={{fontSize:11,fontWeight:600,color:'#64748b',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:8}}>Certificates</div>
                          {appCerts.map(cert => {
                            const pdfUrl = getPdfUrl(cert.certificatePdfPath);
                            return (
                              <div key={cert.id} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',background:'rgba(129,140,248,0.05)',border:'1px solid rgba(129,140,248,0.12)',borderRadius:8,marginBottom:6}}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2">
                                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                                </svg>
                                <div style={{flex:1,fontSize:13,color:'#cbd5e1'}}>{cert.softwareName}</div>
                                <StatusBadge status={cert.status}/>
                                {pdfUrl && (
                                  <a
                                    href={pdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{display:'flex',alignItems:'center',gap:6,padding:'6px 14px',background:'rgba(129,140,248,0.15)',border:'1px solid rgba(129,140,248,0.3)',borderRadius:6,color:'#818cf8',fontSize:12,fontWeight:600,textDecoration:'none',cursor:'pointer'}}
                                    onClick={e => e.stopPropagation()}
                                  >
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                                    </svg>
                                    Download Certificate
                                  </a>
                                )}
                              </div>
                            );
                          })}

                          <div style={{marginTop:8,padding:'10px 14px',background:'rgba(245,158,11,0.05)',border:'1px solid rgba(245,158,11,0.15)',borderRadius:8,display:'flex',alignItems:'center',gap:12}}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
                              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                            </svg>
                            <span style={{flex:1,fontSize:13,color:'#cbd5e1'}}>Product License Certificate (TRDATA AI)</span>
                            <a
                              href="/uploads/certificates/Product_License_Riset.pdf"
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{display:'flex',alignItems:'center',gap:6,padding:'6px 14px',background:'rgba(245,158,11,0.15)',border:'1px solid rgba(245,158,11,0.3)',borderRadius:6,color:'#f59e0b',fontSize:12,fontWeight:600,textDecoration:'none'}}
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                              </svg>
                              Download License PDF
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
